import graphqlClient from './graphqlClient';
import { environment } from '../environment/environment';

export interface BrandFile {
  id: string;
  name: string;
  type: string;
  size: number;
  uploadedBy: string;
  uploadedAt: string;
  description: string | null;
  brandName: string;
}

export interface FetchBrandFilesResult {
  files: BrandFile[];
  totalCount: number;
}

export interface BrandFileFilters {
  name?: string;
  uploadedBy?: string;
  dateFrom?: string;
  dateTo?: string;
  brandNames?: string[];
}

function mapGqlRecord(row: any): BrandFile {
  return {
    id: row.id,
    name: row.original_filename,
    type: row.mime_type,
    size: row.file_size_bytes,
    uploadedBy: row.uploaded_by,
    uploadedAt: row.created_at,
    description: row.description || null,
    brandName: row.brand_name,
  };
}

/**
 * Build the inner conditions for a Hasura where clause.
 * Returns empty string when no conditions, otherwise returns the inner content
 * e.g. `brand_name: { _eq: "Acme" }, category: { _eq: "Reports" }`
 */
function buildWhereConditions(filters?: BrandFileFilters, brandName?: string, isUltimateParent?: boolean): string {
  const conditions: string[] = [];

  if (brandName) {
    if (isUltimateParent) {
      conditions.push(`_or: [{ brand_name: { _eq: "${brandName}" } }, { ultimate_parent: { _eq: "${brandName}" } }]`);
    } else {
      conditions.push(`brand_name: { _eq: "${brandName}" }`);
    }
  }

  if (filters) {
    if (filters.brandNames && filters.brandNames.length > 0) {
      const namesArr = filters.brandNames.map(n => `"${n}"`).join(', ');
      conditions.push(`_or: [{ brand_name: { _in: [${namesArr}] } }, { ultimate_parent: { _in: [${namesArr}] } }]`);
    }
    if (filters.name) conditions.push(`original_filename: { _ilike: "%${filters.name}%" }`);
    if (filters.uploadedBy) conditions.push(`uploaded_by: { _ilike: "%${filters.uploadedBy}%" }`);
    if (filters.dateFrom) conditions.push(`created_at: { _gte: "${filters.dateFrom}" }`);
    if (filters.dateTo) conditions.push(`created_at: { _lte: "${filters.dateTo}" }`);
  }

  if (conditions.length === 0) return '';
  return conditions.join(', ');
}

export async function fetchBrandFiles(
  brandName: string,
  isUltimateParent = false,
  limit = 20,
  offset = 0,
  filters?: BrandFileFilters,
): Promise<FetchBrandFilesResult> {
  try {
    const inner = buildWhereConditions(filters, brandName, isUltimateParent);
    const whereClause = inner ? `where: { ${inner} }` : '';

    const query = `query GetBrandFiles {
  app_brand_files(limit: ${limit}, offset: ${offset}, order_by: { created_at: desc }${inner ? `, ${whereClause}` : ''}) {
    id
    brand_name
    ultimate_parent
    original_filename
    file_size_bytes
    mime_type
    description
    uploaded_by
    created_at
  }
  app_brand_files_aggregate${inner ? `(${whereClause})` : ''} {
    aggregate {
      count
    }
  }
}`;

    const response = await graphqlClient.request<{
      app_brand_files: any[];
      app_brand_files_aggregate: { aggregate: { count: number } };
    }>(query);

    return {
      files: response.app_brand_files.map(mapGqlRecord),
      totalCount: response.app_brand_files_aggregate.aggregate.count,
    };
  } catch (error) {
    console.error('fetchBrandFiles error:', error);
    return { files: [], totalCount: 0 };
  }
}

export async function fetchAllBrandFiles(
  limit = 20,
  offset = 0,
  filters?: BrandFileFilters,
): Promise<FetchBrandFilesResult> {
  try {
    const inner = buildWhereConditions(filters);
    const whereClause = inner ? `where: { ${inner} }` : '';

    const query = `query GetAllBrandFiles {
  app_brand_files(limit: ${limit}, offset: ${offset}, order_by: { created_at: desc }${inner ? `, ${whereClause}` : ''}) {
    id
    brand_name
    ultimate_parent
    original_filename
    file_size_bytes
    mime_type
    description
    uploaded_by
    created_at
  }
  app_brand_files_aggregate${inner ? `(${whereClause})` : ''} {
    aggregate {
      count
    }
  }
}`;

    const response = await graphqlClient.request<{
      app_brand_files: any[];
      app_brand_files_aggregate: { aggregate: { count: number } };
    }>(query);

    return {
      files: response.app_brand_files.map(mapGqlRecord),
      totalCount: response.app_brand_files_aggregate.aggregate.count,
    };
  } catch (error) {
    console.error('fetchAllBrandFiles error:', error);
    return { files: [], totalCount: 0 };
  }
}

// --- REST API helpers (upload, delete, download need NestJS for GCS) ---

function getApiBaseUrl(): string {
  const gqlUrl = new URL(environment.graphqlEndpoint);
  if (gqlUrl.hostname === 'localhost' || gqlUrl.hostname === '127.0.0.1') {
    return `${gqlUrl.protocol}//localhost:8000`;
  }
  return `${gqlUrl.origin}/api`;
}

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function uploadBrandFile(
  brandName: string,
  file: File,
  description: string,
  uploadedBy: string,
  ultimateParent?: string,
  onProgress?: (percent: number) => void,
  onXhrReady?: (xhr: XMLHttpRequest) => void,
): Promise<BrandFile> {
  const base = getApiBaseUrl();
  const authHeaders = getAuthHeaders();

  // Step 1: Get a signed GCS upload URL from NestJS
  const signedRes = await fetch(`${base}/api/brand-files/signed-upload-url`, {
    method: 'POST',
    headers: { ...authHeaders, 'Content-Type': 'application/json' },
    body: JSON.stringify({
      brand_name: brandName,
      filename: file.name,
      mime_type: file.type,
      file_size: file.size,
      ultimate_parent: ultimateParent || undefined,
      description: description || undefined,
      uploaded_by: uploadedBy,
    }),
  });

  if (!signedRes.ok) {
    const err = await signedRes.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to get upload URL');
  }

  const { uploadUrl, filePath } = await signedRes.json();

  // Step 2: PUT the file directly to GCS (fast — no double hop through server)
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', file.type);

    if (onXhrReady) onXhrReady(xhr);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`GCS upload failed: ${xhr.status} ${xhr.statusText}`));
    };
    xhr.onabort = () => reject(new Error('Upload cancelled'));
    xhr.onerror = () => reject(new Error('Upload failed: network error'));
    xhr.send(file);
  });

  // Step 3: Confirm with NestJS so it saves the DB record
  const confirmRes = await fetch(`${base}/api/brand-files/confirm-upload`, {
    method: 'POST',
    headers: { ...authHeaders, 'Content-Type': 'application/json' },
    body: JSON.stringify({
      brand_name: brandName,
      file_path: filePath,
      original_filename: file.name,
      mime_type: file.type,
      file_size: file.size,
      ultimate_parent: ultimateParent || undefined,
      description: description || undefined,
      uploaded_by: uploadedBy,
    }),
  });

  if (!confirmRes.ok) {
    const err = await confirmRes.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to confirm upload');
  }

  const json = await confirmRes.json();
  return mapGqlRecord(json.data);
}

export async function deleteBrandFile(fileId: string): Promise<boolean> {
  const base = getApiBaseUrl();

  const response = await fetch(`${base}/api/brand-files/${fileId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });

  return response.ok;
}

export async function getDownloadUrl(fileId: string): Promise<string | null> {
  const base = getApiBaseUrl();

  const response = await fetch(`${base}/api/brand-files/${fileId}/download`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) return null;
  const json = await response.json();
  return json.url || null;
}
